import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import type { PersonnelDetail, PersonnelRecognition } from '../personnelTypes';

/**
 * Recognition — CS Form 212, section VIII, item 32.
 *
 * **Captured from the hospital system's own Recognition module**, and filed directly below
 * Special skills/Hobbies because item 32 follows item 31 on the form.
 *
 * **MOST RECENT first, and rows nobody dated LAST.** This reads like the histories above it
 * rather than like the skills beside it: an award is a moment, so the newest is what a reader is
 * looking for. An undated row sorts to the bottom whichever way the column is pointed — null
 * there means nobody wrote the date down, and floating those to the top would present them as the
 * most recent, which is a claim the record does not make.
 *
 * **Searched, sorted and paged in memory**, which is correct here: the rule against client-side
 * filtering is about a PAGED list whose filter applies only to the rows on screen. These arrive
 * whole with the record, so the filter sees everything there is.
 */
export function RecognitionPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelRecognition) => void;
  onDelete: (row: PersonnelRecognition) => void;
  onBulkDelete: (rows: PersonnelRecognition[]) => void;
  onRestore: (row: PersonnelRecognition) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.recognitions : record.recognitions.filter((x) => x.isActive)),
    [record.recognitions, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [x.title, x.awardedBy, x.description]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'dateAwarded';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'dateAwarded') {
          /*
            Undated rows sort LAST whichever way the column points — null here means nobody wrote
            the date down, and burying those among the dated ones would read as a date the record
            does not carry. The same call the L&D panel's own dates take.

            Compared as STRINGS, and that is safe precisely because these are `YYYY-MM-DD`
            calendar dates: they sort lexicographically in date order, and parsing them into
            `Date` would put a calendar date through the instant path — the one thing the
            formatDateOnly rule exists to prevent.
          */
          const av = a.dateAwarded ?? null;
          const bv = b.dateAwarded ?? null;
          if (av === null && bv === null) return a.title.localeCompare(b.title);
          if (av === null) return 1;
          if (bv === null) return -1;
          const gap = av.localeCompare(bv) * direction;
          return gap !== 0 ? gap : a.title.localeCompare(b.title);
        }

        const pick = (row: PersonnelRecognition) =>
          field === 'awardedBy'
            ? (row.awardedBy ?? '')
            : field === 'description'
              ? (row.description ?? '')
              : row.title;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelRecognition>({
    fetchPage,
    pageSize: 10,

    // Most recent first, matching the order the server sends them in — or the profile and its own
    // table would disagree about which row is first.
    defaultSort: '-dateAwarded',
  });

  const columns: DataColumn<PersonnelRecognition>[] = [
    {
      /*
        The ANCHOR is the award, which is what identifies the row. Not the awarding body, which
        titles four rows "Department of Health" and tells a reader nothing about which entry they
        are looking at — the mistake the family and education panels each made first.
      */
      key: 'title',
      header: 'Recognition',
      width: 260,
      sortable: true,
      render: (x) => <TextCell value={x.title} />,
    },
    {
      key: 'awardedBy',
      header: 'Awarded by',
      width: 200,
      sortable: true,
      summary: true,
      /*
        Plain text, not mono: an organization's name is a name, not a machine value. A stored
        value with no link to anything — the awarding body is almost never this organization.
      */
      render: (x) => <TextCell value={x.awardedBy ?? null} />,
    },
    {
      key: 'dateAwarded',
      header: 'Date awarded',
      width: 150,
      sortable: true,
      summary: true,
      /*
        `formatDateOnly` through a `<TextCell mono />`, never `<DateCell>`: this is a CALENDAR
        date and `<DateCell>` renders an instant with a time. Put through the instant path, an
        award dated 01 January reads as 31 December for a reader west of UTC.
      */
      render: (x) => <TextCell value={x.dateAwarded ? formatDateOnly(x.dateAwarded) : null} mono />,
    },
    {
      key: 'description',
      header: 'Citation',
      width: 300,
      sortable: true,
      render: (x) => <TextCell value={x.description ?? null} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelRecognition>
        id="personnel-recognition"
        state={state}
        /*
          The first column opens the row as a key/value preview, built from these same `columns` so
          it cannot drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Recognition', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search award, awarding body or citation"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not something
          the reader is part-way through, it is simply not an action here.
        */
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default — `selectable={false}` is only for a
          table nothing can ever be done to, never for one whose bulk action is merely unbuilt.
          So the bulk action ships with it.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-recognitions',
              label: `Delete ${deletable.length} recognition${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected recognition is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No recognitions recorded yet."
        emptySearchMessage="No recognition matches that search."
      />
    </ProfileSection>
  );
}
